import {
  getBusinessWifiPriceSelections,
  getBusinessWifiTotals
} from './businessWifi/businessWifi';

import {
  getCustomerInformationTotals,
  getCustomerInformationPriceSelections
} from './customerInformation/customerInformations';

import {
  getFibreInternetTotals,
  getFibreInternetPriceSelections
} from './fibeInternet/fibreInternet';

import {
  getMobilityTotals,
  getMobilityPriceSelections
} from './mobility/mobility';

import getSummaryConfig, { GET_FIELD_VALUE } from './utils/summaryConfig';
import getFieldSummaryDetailsForOneOption, {
  getFieldSummaryDetailsFromField,
  getSummaryDetailsForFieldWithCouponCode,
  getSummaryDetailsForFieldWithSelectedOptions
} from './utils/getFieldSummaryDetails';
import {
  getSecureBusinessTotals,
  getSecureBusinessPriceSelections
} from './secureBusiness/secureBusiness';

import removePriceSubstring from './utils/removePriceSubstring';
import { cloneDeep } from 'lodash';
import { getLocale } from '../../misc';
import { preferredLanguageVulcanVariable } from '../../utils/constant';
import {
  getCouponCodePromotion,
  getCouponCodePromotionSelections
} from './couponCodePromotion/couponCodePromotion';

const vulcanPricing = (formFields, orderData, redeemOfferData) => {
  const { priceSelections = {} } = orderData;
  const totalResults = [
    getCustomerInformationTotals(formFields, priceSelections),
    getFibreInternetTotals(formFields, priceSelections),
    getBusinessWifiTotals(formFields, priceSelections),
    getMobilityTotals(formFields, priceSelections),
    getSecureBusinessTotals(formFields, priceSelections),
    getCouponCodePromotion(redeemOfferData, priceSelections)
  ];
  const total = {
    monthly: 0,
    oneTime: 0,
    threeYearEasyPayment: 0,
    fiveYearEasyPayment: 0
  };
  totalResults.forEach(result => {
    total.oneTime += result.oneTime;
    total.monthly +=
      result.monthly + result.threeYearEasyPayment + result.fiveYearEasyPayment;
  });
  return total;
};

export const getVulcanPriceSelections = (formFields, redeemOfferData) => {
  let selections = {};
  const priceSelections = [
    getCustomerInformationPriceSelections(formFields),
    getFibreInternetPriceSelections(formFields),
    getBusinessWifiPriceSelections(formFields),
    getMobilityPriceSelections(formFields),
    getSecureBusinessPriceSelections(formFields),
    getCouponCodePromotionSelections(redeemOfferData)
  ];
  priceSelections.forEach(selection => {
    selections = { ...selections, ...selection };
  });
  return selections;
};

export const getPricingSummary = (formFields, alternateLanguage, redeemOfferData) => {
  if (!formFields) return [];
  return getSummaryConfig(alternateLanguage)
    .filter(summaryConfigSectionItem => {
      return (
        summaryConfigSectionItem.shouldShowSection &&
        summaryConfigSectionItem.shouldShowSection(formFields)
      );
    })
    .map(summaryConfigSectionItem => {
      const total = {
        monthlyCharges: 0,
        oneTimeCharges: 0,
        threeYearEasyPaymentCharges: 0,
        fiveYearEasyPaymentCharges: 0
      };
      return {
        sectionTitle: summaryConfigSectionItem.sectionTitle,
        rows: summaryConfigSectionItem.rows.map(row => {
          let rowDetails = { qty: 0, monthlyCharges: 0, oneTimeCharges: 0 };
          if (row.checkForOption) {
            rowDetails = getFieldSummaryDetailsForOneOption(
              formFields[row.checkForKey],
              row.checkForOption
            );
            const {
              qty,
              monthlyCharges,
              oneTimeCharges,
              threeYearEasyPaymentCharges,
              fiveYearEasyPaymentCharges
            } = rowDetails;
            total.monthlyCharges += monthlyCharges;
            total.oneTimeCharges += oneTimeCharges;
            total.threeYearEasyPaymentCharges += threeYearEasyPaymentCharges;
            total.fiveYearEasyPaymentCharges += fiveYearEasyPaymentCharges;
            return {
              label: row.label,
              description: row.description,
              qty,
              monthlyCharges,
              oneTimeCharges,
              threeYearEasyPaymentCharges,
              fiveYearEasyPaymentCharges
            };
          }
          if (row.checkForKey) {
            rowDetails = getFieldSummaryDetailsFromField(
              formFields[row.checkForKey]
            );
            const {
              qty,
              description,
              monthlyCharges,
              oneTimeCharges,
              threeYearEasyPaymentCharges,
              fiveYearEasyPaymentCharges
            } = rowDetails;
            let returnObj = {
              label: row.label,
              description:
                row.description === GET_FIELD_VALUE
                  ? formFields[row.checkForKey].value
                  : row.description || description,
              qty,
              monthlyCharges,
              oneTimeCharges,
              threeYearEasyPaymentCharges,
              fiveYearEasyPaymentCharges
            };
            if (row.transformer) {
              returnObj = row.transformer(returnObj);
            }
            // Function to check if a field should be removed based on substrings
            const shouldRemove = (fieldName) =>
              row.filterKeySubstringNegativeValues &&
              Array.isArray(row.filterKeySubstringNegativeValues) &&
              row.filterKeySubstringNegativeValues.some((substring) =>
                fieldName.includes(substring)
              );

            // List of fields to process
            const fieldsToProcess = [
              { key: "description", action: "delete" },
              { key: "qty", action: "delete" },
              { key: "monthlyCharges", action: "addToTotal" },
              { key: "oneTimeCharges", action: "addToTotal" },
              { key: "threeYearEasyPaymentCharges", action: "addToTotal" },
              { key: "fiveYearEasyPaymentCharges", action: "addToTotal" }
            ];

            // Process each field
            fieldsToProcess.forEach(({ key, action }) => {
              if (shouldRemove(key)) {
                delete returnObj[key];
              } else if (action === "addToTotal" && returnObj[key] !== undefined) {
                total[key] += returnObj[key];
              }
            });
            return returnObj;
          }
          if (row.listSelectedOptionsForKey) {
            getSummaryDetailsForFieldWithSelectedOptions(
              formFields[row.listSelectedOptionsForKey]
            ).forEach(summaryItem => {
              total.monthlyCharges += summaryItem.monthlyCharges;
              total.oneTimeCharges += summaryItem.oneTimeCharges;
              total.threeYearEasyPaymentCharges +=
                summaryItem.threeYearEasyPaymentCharges;
              total.fiveYearEasyPaymentCharges +=
                summaryItem.fiveYearEasyPaymentCharges;
            });
            return {
              label: row.label,
              rows: getSummaryDetailsForFieldWithSelectedOptions(
                row.getCustomLabel
                  ? row.getCustomLabel(formFields)
                  : formFields[row.listSelectedOptionsForKey]
              )
            };
          }
          if (
            row.listSelectedValuesForEquipment &&
            formFields[row.listSelectedValuesForEquipment.basedOnKey].value
          ) {
            rowDetails = getFieldSummaryDetailsFromField(
              formFields[row.checkForKey]
            );
            const selectedBaseValue =
              formFields[row.listSelectedValuesForEquipment.basedOnKey].value;
            const requiredEquipmentKeys =
              row.listSelectedValuesForEquipment.map[selectedBaseValue];
            const rows = [];
            requiredEquipmentKeys.forEach(key => {
              Object.keys(formFields[key]).forEach(equipmentItem => {
                let shouldCalculate = true;
                const filterKeyBySubstringValue =
                  row.listSelectedValuesForEquipment.filterKeysBySubstring;
                const filterKeyBySubstringNegativeValues =
                  row.listSelectedValuesForEquipment
                    .filterKeysBySubstringNegative;
                if (filterKeyBySubstringValue) {
                  shouldCalculate = [filterKeyBySubstringValue].flat().some(substring => {
                    return equipmentItem.includes(substring);
                  });
                }
                if (filterKeyBySubstringNegativeValues) {
                  shouldCalculate = !filterKeyBySubstringNegativeValues.some(
                    substring => {
                      return equipmentItem.includes(substring);
                    }
                  );
                }
                if (shouldCalculate) {
                  rowDetails = getFieldSummaryDetailsFromField(
                    formFields[key][equipmentItem]
                  );
                  if (+rowDetails.qty)
                    rows.push({
                      ...rowDetails,
                      qty: +rowDetails.qty,
                      description: removePriceSubstring(
                        formFields[key][equipmentItem].fieldLabel || ''
                      ),
                      monthlyCharges:
                        rowDetails.monthlyCharges ||
                        rowDetails.threeYearEasyPaymentCharges ||
                        rowDetails.fiveYearEasyPaymentCharges
                    });
                  total.monthlyCharges +=
                    rowDetails.monthlyCharges +
                    rowDetails.threeYearEasyPaymentCharges +
                    rowDetails.fiveYearEasyPaymentCharges;
                  total.oneTimeCharges += rowDetails.oneTimeCharges;
                  total.threeYearEasyPaymentCharges +=
                    rowDetails.threeYearEasyPaymentCharges;
                  total.fiveYearEasyPaymentCharges +=
                    rowDetails.fiveYearEasyPaymentCharges;
                }
              });
            });
            return {
              label: row.label,
              rows
            };
          }
          if (row.listSelectedOptionsCouponCode) {
            total.monthlyCharges =
              -redeemOfferData.redeemOfferAdjustedTotalPrice.monthlyPrice;
            total.oneTimeCharges =
              -redeemOfferData.redeemOfferAdjustedTotalPrice.oneTimePrice;

            return {
              label: row.label,
              rows: getSummaryDetailsForFieldWithCouponCode(
                row.getCustomLabel
                  ? row.getCustomLabel(formFields)
                  : redeemOfferData
              )
            };
          }
          return {};
        }),
        total
      };
    });
};

export const getFormFieldSummary = (formFields, formFieldsForSummary) => {
  const updatedFormFieldSummary = cloneDeep(formFieldsForSummary);
  if (!updatedFormFieldSummary) return {};
  Object.keys(formFields).map(key => {
    if (
      !['MultiCheckbox', 'Radio'].includes(formFields[key].type) &&
      formFields[key].value
    ) {
      updatedFormFieldSummary[key] = {
        ...updatedFormFieldSummary[key],
        value: formFields[key].value
      };
    } else if (
      ['MultiCheckbox', 'Radio'].includes(formFields[key].type) &&
      formFields[key].value
    ) {
      formFields[key].options.map((field, index) => {
        if (Array.isArray(formFields[key].value)) {
          if (!Array.isArray(updatedFormFieldSummary[key].value)) {
            updatedFormFieldSummary[key].value = [];
          }
          if (formFields[key].value.includes(field.fields.value)) {
            if (formFields[key].name === 'secureBusinessOffersVulcan') {
              if (field.fields.monthlyPayment) {
                updatedFormFieldSummary[key].options[
                  index
                ].fields.monthlyPayment = field.fields.monthlyPayment;
              }
              if (field.fields.oneTimePrice) {
                updatedFormFieldSummary[key].options[
                  index
                ].fields.oneTimePrice = field.fields.oneTimePrice;
              }
            }
            updatedFormFieldSummary[key] = {
              ...updatedFormFieldSummary[key],
              value: [
                ...updatedFormFieldSummary[key].value,
                formFieldsForSummary[key].options[index].fields.value
              ]
            };
          }
        } else {
          if (formFields[key].value === field.fields.value) {
            // This condition ensures that the logic to update `monthlyPayment` is only applied
            // to the field with the name 'secureBusinessPlanVulcan'. Without this check,
            // the logic would run for all radio fields, which could lead to unintended updates
            if (formFields[key].name === 'secureBusinessPlanVulcan') {
              updatedFormFieldSummary[key].options.map((field, index) => {
                field.fields.monthlyPayment =
                  formFields[key].options[index].fields.monthlyPayment;
              });
            }
            if (updatedFormFieldSummary[key]) {
              updatedFormFieldSummary[key] = {
                ...updatedFormFieldSummary[key],
                value: formFieldsForSummary[key].options[index].fields.value
              };
            }
          }
        }
      });
    } else if (!formFields[key].type) {
      Object.keys(formFields[key]).forEach(field => {
        if (formFields[key][field].value) {
          updatedFormFieldSummary[key][field] = {
            ...updatedFormFieldSummary[key][field],
            value: formFields[key][field].value
          };
        }
      });
    }
  });
  return updatedFormFieldSummary;
};

export const getLocalLanguageIsDifferent = formFields => {
  const locale = getLocale();
  if (formFields['preferredLanguageVulcan']) {
    if (
      preferredLanguageVulcanVariable[locale].includes(
        formFields['preferredLanguageVulcan'].value
      )
    ) {
      return false;
    }
  }
  return true;
};

export default vulcanPricing;
